/**
 * Utility functions for Azure Referral Form
 */
import type {
  AzureMpcReferralType,
  DealType,
  ReferralStatus,
  ReferralSubstatus,
} from './azureReferralSchema'
import {
  AZURE_CUSTOMER_PURCHASE_INTENT,
  AZURE_DEAL_TYPE,
  AZURE_FORM_STATUS,
  AZURE_MPC_REFERRAL_TYPE,
  AZURE_REFERRAL_STATUS,
  AZURE_REFERRAL_SUBSTATUS,
  SHARED_AZURE_DEAL_TYPES,
  type AzureCustomerPurchaseIntent,
  type AzureFormStatus,
  isAzureDealType,
  isTerminalReferralSubstatus,
  matchesReferralStatus,
  matchesReferralSubstatus,
} from './azureReferralSchema'
import { isSharedCosellDealType } from './dealTypeUtils'
import { getHubspotCosellAccountIdFromPath } from '../../../../common/utils/Hooks/partnerStateSelectors'
import { CustomerDetails, DealDetails, HelpFromMicrosoft } from './types'

type AzureReferralMetadataSlice = {
  partnerId?: string
  partner_id?: string
  referralId?: string
  crmPrimaryKeyValue?: string
}

type ResolveAzurePartnerIdParams = {
  pathname: string
  accountIdFromPath?: string
  tenantPartnerId?: string
  azureReferralMetadata?: AzureReferralMetadataSlice
}

export const resolveAzurePartnerId = ({
  pathname,
  accountIdFromPath,
  tenantPartnerId,
  azureReferralMetadata,
}: ResolveAzurePartnerIdParams): string =>
  getHubspotCosellAccountIdFromPath(pathname) ||
  accountIdFromPath ||
  azureReferralMetadata?.partnerId ||
  azureReferralMetadata?.partner_id ||
  tenantPartnerId ||
  ''

export const isAzureReferralRouteReady = (
  referralId: string | undefined,
  filledForm: AzureReferralFormHydrationSlice,
  azureReferralMetadata?: AzureReferralMetadataSlice
): boolean =>
  Boolean(referralId) &&
  (hasAzureReferralFormMappedData(filledForm) ||
    Boolean(
      azureReferralMetadata?.referralId ||
        azureReferralMetadata?.crmPrimaryKeyValue
    ))

type AzureReferralFormHydrationSlice = {
  customerDetails?: CustomerDetails
  dealDetails?: DealDetails
}

export { AZURE_MPC_REFERRAL_TYPE }
export type { AzureMpcReferralType, DealType }

/** Canonical form option for "no Microsoft help". */
export const NO_HELP_REQUIRED = 'No help required at this time'

/**
 * CRM / mapping-template variants that mean the same as {@link NO_HELP_REQUIRED}.
 * Mapping service picklists historically use "at this point of time".
 */
const NO_HELP_REQUIRED_ALIASES = new Set([
  'no help required at this time',
  'no help required at this point of time',
])

const normalizeTypeOfHelpKey = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, ' ')

export const isNoMicrosoftHelpRequired = (typeOfHelp?: string): boolean => {
  if (typeof typeOfHelp !== 'string') return false
  const key = normalizeTypeOfHelpKey(typeOfHelp)
  return Boolean(key) && NO_HELP_REQUIRED_ALIASES.has(key)
}

/**
 * Map CRM / mapping-template type-of-help onto the form option set.
 * Unknown non-empty values are returned trimmed so Select can still display them.
 */
export const normalizeAzureTypeOfHelp = (
  value: unknown
): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  if (isNoMicrosoftHelpRequired(trimmed)) return NO_HELP_REQUIRED
  return trimmed
}

export const hasAzureReferralFormMappedData = (
  filledForm: AzureReferralFormHydrationSlice
): boolean =>
  Boolean(
    filledForm.dealDetails?.dealName ||
      filledForm.customerDetails?.customerName ||
      filledForm.dealDetails?.crmId
  )

export const getAzureReferralFormikKey = (
  referralId: string | undefined,
  filledForm: AzureReferralFormHydrationSlice
): string => {
  if (!referralId) {
    return 'new-azure-referral'
  }

  return hasAzureReferralFormMappedData(filledForm)
    ? `${referralId}-hydrated`
    : `${referralId}-pending`
}

export const isAzureReferralFormLoadPending = (
  referralId: string | undefined,
  filledForm: AzureReferralFormHydrationSlice
): boolean => Boolean(referralId) && !hasAzureReferralFormMappedData(filledForm)

export const isMicrosoftHelpRequested = (typeOfHelp?: string) =>
  Boolean(typeof typeOfHelp === 'string' && typeOfHelp.trim()) &&
  !isNoMicrosoftHelpRequired(typeOfHelp)

/**
 * Deal types driven by seller visibility when no Microsoft help is requested.
 */
export const getDealTypeForSellerDealView = (
  sellerDealView: NonNullable<HelpFromMicrosoft['sellerDealView']>
): DealType =>
  sellerDealView === 'YES'
    ? AZURE_DEAL_TYPE.PARTNER_LED
    : AZURE_DEAL_TYPE.PRIVATE

/**
 * Normalize CRM / legacy / form purchase-intent values to Microsoft
 * `CustomerMarketplaceIntent` enums (also used as form radio keys).
 */
export const mapAzureCustomerPurchaseIntent = (
  value: unknown
): AzureCustomerPurchaseIntent | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return undefined
  }
  const normalized = trimmed.toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ')
  if (normalized === 'yes') {
    return AZURE_CUSTOMER_PURCHASE_INTENT.YES
  }
  if (normalized === 'no') {
    return AZURE_CUSTOMER_PURCHASE_INTENT.NO
  }
  if (
    normalized === 'have not decided' ||
    normalized === 'havenotdecided' ||
    normalized === 'not decided'
  ) {
    return AZURE_CUSTOMER_PURCHASE_INTENT.HAVE_NOT_DECIDED
  }
  return undefined
}

export const mapFormDealTypeToReferralType = (
  dealType?: DealType | string
): AzureMpcReferralType =>
  dealType && (SHARED_AZURE_DEAL_TYPES as readonly string[]).includes(dealType)
    ? AZURE_MPC_REFERRAL_TYPE.SHARED
    : AZURE_MPC_REFERRAL_TYPE.INDEPENDENT

export { isAzureDealType }

const emptyHelpContactFields: Pick<
  HelpFromMicrosoft,
  'firstName' | 'lastName' | 'phone' | 'email'
> = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
}

/**
 * Updates Help from Microsoft when type of help changes.
 * - Help requested: clear seller visibility (follow-up is hidden).
 * - No help / empty: preserve seller visibility if already set (e.g. mapped);
 *   do not invent a default — user must choose Yes/No on create.
 *
 * Customer contact fields are stored on `customerDetails` and shared with
 * Customer Details; they are not cleared when help type changes.
 */
export const getHelpFromMicrosoftForTypeOfHelp = ({
  helpFromMicrosoft,
  typeOfHelp,
}: {
  helpFromMicrosoft: HelpFromMicrosoft
  typeOfHelp: HelpFromMicrosoft['typeOfHelp']
}): HelpFromMicrosoft => {
  if (isMicrosoftHelpRequested(typeOfHelp)) {
    return {
      ...helpFromMicrosoft,
      typeOfHelp,
      sellerDealView: undefined,
      ...emptyHelpContactFields,
    }
  }

  return {
    ...helpFromMicrosoft,
    typeOfHelp,
    ...emptyHelpContactFields,
    sellerDealView: helpFromMicrosoft.sellerDealView,
  }
}

export type AzureFormEditabilityOptions = {
  dealType?: DealType | string
  /** Current Help from Microsoft type — used for Active + Accepted help-field rules */
  typeOfHelp?: string
  /** Sales stage label/value; Blocked is treated as a terminal non-editable state */
  salesStage?: string | number
}

const isNewPendingReferral = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string
) =>
  matchesReferralStatus(status, AZURE_REFERRAL_STATUS.NEW) &&
  matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.PENDING)

const isActiveAcceptedReferral = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string
) =>
  matchesReferralStatus(status, AZURE_REFERRAL_STATUS.ACTIVE) &&
  matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.ACCEPTED)

const isBlockedSalesStage = (salesStage?: string | number) =>
  String(salesStage ?? '')
    .trim()
    .toLowerCase() === 'blocked'

const isErrorReferralStatus = (status?: ReferralStatus | string) =>
  status?.toLowerCase() === 'error'

/** Fields editable while Active + Accepted (post Microsoft acceptance). */
const ACTIVE_ACCEPTED_EDITABLE_FIELDS = [
  'estimatedCloseDate',
  'estimatedValue',
  'currency',
  'marketingCampaignID',
  'partnerNotes',
  'notesToMicrosoft',
  'teamMembers',
  'contactDetails',
  'solutionsOffered',
  'companySolutions',
  'microsoftSolutions',
  'selectedSolutions',
  'typeOfHelp',
  'sellerDealView',
  'salesStage',
  'status',
  'substatus',
] as const

/** Progression fields editable whenever the form itself is editable. */
const GENERAL_EDITABLE_FIELDS = [
  'dealName',
  'estimatedCloseDate',
  'estimatedValue',
  'currency',
  'partnerNotes',
  'notesToMicrosoft',
  'crmId',
  'marketingCampaignID',
  'teamMembers',
  'contactDetails',
  'solutionsOffered',
  'companySolutions',
  'microsoftSolutions',
  'selectedSolutions',
  'typeOfHelp',
  'sellerDealView',
] as const

export const isFormEditable = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  formStatus?: AzureFormStatus | string,
  options?: AzureFormEditabilityOptions
): boolean => {
  if (formStatus === AZURE_FORM_STATUS.DRAFT) {
    return true
  }

  if (isBlockedSalesStage(options?.salesStage)) {
    return false
  }

  if (!status) {
    // If no status is set, allow editing (new referral create flow)
    return true
  }

  if (isErrorReferralStatus(status)) {
    return false
  }

  if (isTerminalReferralSubstatus(substatus)) {
    return false
  }

  if (matchesReferralStatus(status, AZURE_REFERRAL_STATUS.CLOSED)) {
    return false
  }

  if (isNewPendingReferral(status, substatus)) {
    // Submitted + awaiting Microsoft: freeze shared (IP / Services) deals only.
    // Private and Partner-led remain editable.
    return !isSharedCosellDealType(options?.dealType)
  }

  if (
    matchesReferralStatus(status, AZURE_REFERRAL_STATUS.NEW) &&
    matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.RECEIVED)
  ) {
    return true
  }

  if (isActiveAcceptedReferral(status, substatus)) {
    return true
  }

  // All other combinations — read-only
  return false
}

/**
 * Creation / setup fields (customer, deal type, solution area, etc.).
 * Locked once Microsoft has accepted the referral (Active + Accepted).
 */
export const areAzureReferralCreationFieldsEditable = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  formStatus?: AzureFormStatus | string,
  options?: AzureFormEditabilityOptions
): boolean => {
  if (!isFormEditable(status, substatus, formStatus, options)) {
    return false
  }

  return !isActiveAcceptedReferral(status, substatus)
}

/**
 * Determines if a specific field is editable based on status and field name
 */
export const isFieldEditable = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  fieldName?: string,
  formStatus?: AzureFormStatus | string,
  options?: AzureFormEditabilityOptions
): boolean => {
  if (!isFormEditable(status, substatus, formStatus, options)) {
    return false
  }

  // If no field name provided, use general form editability
  if (!fieldName) {
    return true
  }

  if (isActiveAcceptedReferral(status, substatus)) {
    if (fieldName === 'typeOfHelp') {
      // Help from Microsoft may be changed only while still "No help required".
      return !isMicrosoftHelpRequested(options?.typeOfHelp)
    }

    return (ACTIVE_ACCEPTED_EDITABLE_FIELDS as readonly string[]).includes(
      fieldName
    )
  }

  return (GENERAL_EDITABLE_FIELDS as readonly string[]).includes(fieldName)
}

/**
 * Seller visibility may be enabled after acceptance (Private → Partner-led),
 * but cannot be removed once set to visible.
 */
export const canChangeAzureSellerDealView = ({
  status,
  substatus,
  formStatus,
  dealType,
  sellerDealView,
}: {
  status?: ReferralStatus | string
  substatus?: ReferralSubstatus | string
  formStatus?: AzureFormStatus | string
  dealType?: DealType | string
  sellerDealView?: HelpFromMicrosoft['sellerDealView']
}): boolean => {
  const options = { dealType }
  if (!isFormEditable(status, substatus, formStatus, options)) {
    return false
  }

  const hasExistingReferral = Boolean(status || substatus)
  if (!hasExistingReferral) {
    return true
  }

  if (!isActiveAcceptedReferral(status, substatus)) {
    return false
  }

  // Active + Accepted: allow enabling visibility only while still Private / No.
  return sellerDealView === 'NO' || dealType === AZURE_DEAL_TYPE.PRIVATE
}

/**
 * Gets a user-friendly message explaining why the form is read-only*
 */
export const getReadOnlyMessage = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  formStatus?: AzureFormStatus | string,
  options?: AzureFormEditabilityOptions
): string => {
  if (isFormEditable(status, substatus, formStatus, options)) {
    return ''
  }

  if (isBlockedSalesStage(options?.salesStage)) {
    return 'This referral is blocked and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.WON)) {
    return 'This referral has been won and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.LOST)) {
    return 'This referral has been lost and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.DECLINED)) {
    return 'This referral has been declined and cannot be edited.'
  }

  if (
    matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.ERROR) ||
    isErrorReferralStatus(status)
  ) {
    return 'This referral has an error and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.EXPIRED)) {
    return 'This referral has expired and cannot be edited. Referrals automatically expire after 14 days of inactivity.'
  }

  if (matchesReferralStatus(status, AZURE_REFERRAL_STATUS.CLOSED)) {
    return 'This referral is closed and cannot be edited.'
  }

  if (isNewPendingReferral(status, substatus)) {
    return 'This referral has been submitted to Microsoft and cannot be edited while it is pending review.'
  }

  return 'This referral is not in an editable state.'
}

export {
  isActiveAcceptedReferral,
  isNewPendingReferral,
  isSharedCosellDealType,
}
